import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Button, Divider, MenuItem
} from '@mui/material';
import { ArrowBack, Add, Edit, Delete, CheckCircle, CalendarMonth, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { isRealDate } from '../utils/dateValidation';

const API = process.env.REACT_APP_API_URL;

const emptyTerm = (termNo) => ({
  termNo,
  termName: `Term ${termNo}`,
  startDate: '',
  endDate: '',
  isCurrent: false
});

const emptyForm = () => ({
  year: '',
  terms: [emptyTerm(1), emptyTerm(2), emptyTerm(3)]
});

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const AcademicYearList = () => {
  const navigate = useNavigate();
  const [years,    setYears]    = useState([]);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [form,     setForm]     = useState(emptyForm());
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving,   setSaving]   = useState(false);
  const [viewYear, setViewYear] = useState(null);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchYears = async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/years`, { headers });
      setYears(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load academic years');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchYears(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2500);
    return () => clearTimeout(t);
  }, [success]);

  const handleCreate = () => {
    setEditingYear(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const handleEdit = (y) => {
    setEditingYear(y.year);
    setForm({
      year: y.year,
      terms: y.terms.length ? y.terms.map(t => ({
        termNo:    t.termNo,
        termName:  t.termName,
        startDate: t.startDate ? t.startDate.split('T')[0] : '',
        endDate:   t.endDate   ? t.endDate.split('T')[0]   : '',
        isCurrent: !!t.isCurrent
      })) : [emptyTerm(1), emptyTerm(2), emptyTerm(3)]
    });
    setDialogOpen(true);
  };

  const handleDelete = (year) => {
    setConfirmDlg(year);
  };

  const confirmAction = async () => {
    try {
      await axios.delete(`${API}/api/academics/years/${confirmDlg}`, { headers });
      setSuccess(`Year ${confirmDlg} deleted`);
      setConfirmDlg(null);
      fetchYears();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
      setConfirmDlg(null);
    }
  };

  const handleActivate = async (year) => {
    try {
      await axios.put(`${API}/api/academics/years/${year}/activate`, {}, { headers });
      setSuccess(`${year} set as active academic year`);
      fetchYears();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate');
    }
  };

  const handleSetCurrentTerm = async (year, termNo) => {
    try {
      const { data } = await axios.put(`${API}/api/academics/years/${year}/current-term/${termNo}`, {}, { headers });
      setSuccess(`Term ${termNo} set as the current term`);
      setViewYear(data);   // refresh the open dialog with updated flags
      fetchYears();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set current term');
    }
  };

  const updateTerm = (termNo, field, value) => {
    setForm(f => ({
      ...f,
      terms: f.terms.map(t => t.termNo === termNo ? { ...t, [field]: value } : t)
    }));
  };

  // Only one term can be "current" — selecting one clears the flag on the rest.
  const setCurrentTermNo = (termNo) => {
    setForm(f => ({
      ...f,
      terms: f.terms.map(t => ({ ...t, isCurrent: String(t.termNo) === String(termNo) }))
    }));
  };

  const handleSave = async () => {
    if (!form.year.trim()) { setError('Year is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        year: form.year.trim(),
        terms: form.terms.map(t => ({
          termNo:    t.termNo,
          termName:  t.termName,
          startDate: t.startDate || undefined,
          endDate:   t.endDate   || undefined,
          isCurrent: !!t.isCurrent
        }))
      };
      if (editingYear) {
        await axios.put(`${API}/api/academics/years/${editingYear}`, payload, { headers });
        setSuccess(`Year ${editingYear} updated`);
      } else {
        await axios.post(`${API}/api/academics/years`, payload, { headers });
        setSuccess(`Year ${form.year} created`);
      }
      setDialogOpen(false);
      fetchYears();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  if (loading) return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarMonth color="primary" />
              <Box>
                <Typography variant="h5" color="primary">Academic Years</Typography>
                <Typography variant="body2" color="text.secondary">
                  Set up years and terms · One active year at a time
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => navigate('/academics')} title="Back to Academics">
                <ArrowBack />
              </IconButton>
              <IconButton onClick={handleCreate} title="New Academic Year"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {years.length === 0 ? (
            <Typography color="text.secondary">No academic years yet. Click + to create one.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Year</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Terms</strong></TableCell>
                  <TableCell><strong>Term 1</strong></TableCell>
                  <TableCell><strong>Term 2</strong></TableCell>
                  <TableCell><strong>Term 3</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {years.map(y => (
                  <TableRow key={y.year} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewYear(y)}>
                    <TableCell><strong>{y.year}</strong></TableCell>
                    <TableCell>
                      <Chip
                        label={y.isActive ? 'Active' : 'Inactive'}
                        color={y.isActive ? 'success' : 'default'}
                        size="small" variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">{y.terms?.length || 0}</TableCell>
                    {[1, 2, 3].map(n => {
                      const t = y.terms?.find(t => t.termNo === n);
                      return (
                        <TableCell key={n}>
                          <Typography variant="caption" color="text.secondary">
                            {t ? `${formatDate(t.startDate)} → ${formatDate(t.endDate)}` : '—'}
                          </Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell onClick={e => e.stopPropagation()}>
                      {!y.isActive && (
                        <IconButton size="small" color="success" title="Set as Active"
                          onClick={() => handleActivate(y.year)}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" color="primary" title="Edit"
                        onClick={() => handleEdit(y)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" title="Delete"
                        onClick={() => handleDelete(y.year)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── View card ── */}
      <Dialog open={Boolean(viewYear)} onClose={() => setViewYear(null)} maxWidth="sm" fullWidth>
        {viewYear && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarMonth color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>Academic Year {viewYear.year}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {viewYear.terms?.length || 0} terms configured
                  </Typography>
                </Box>
                <Box ml="auto">
                  <Chip
                    label={viewYear.isActive ? 'Active' : 'Inactive'}
                    color={viewYear.isActive ? 'success' : 'default'}
                    size="small" variant="outlined"
                  />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {viewYear.terms?.map(t => (
                <Box key={t.termNo} mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="subtitle2" color="primary">
                      {t.termName}
                    </Typography>
                    {t.isCurrent
                      ? <Chip label="Current term" size="small" color="success" />
                      : viewYear.isActive && (
                          <Button size="small" variant="text" onClick={() => handleSetCurrentTerm(viewYear.year, t.termNo)}>
                            Set as current
                          </Button>
                        )}
                  </Box>
                  <InfoRow label="Start Date" value={formatDate(t.startDate)} />
                  <InfoRow label="End Date"   value={formatDate(t.endDate)} />
                  {t.termNo < 3 && <Divider sx={{ mt: 1 }} />}
                </Box>
              ))}
              {(!viewYear.terms || viewYear.terms.length === 0) && (
                <Typography color="text.secondary">No terms configured yet.</Typography>
              )}
            </DialogContent>
            <DialogActions>
              {!viewYear.isActive && (
                <Button startIcon={<CheckCircle />} color="success"
                  onClick={() => { setViewYear(null); handleActivate(viewYear.year); }}>
                  Set Active
                </Button>
              )}
              <Button startIcon={<Edit />}
                onClick={() => { setViewYear(null); handleEdit(viewYear); }}>
                Edit
              </Button>
              <Button variant="outlined" onClick={() => setViewYear(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete academic year <strong>{confirmDlg}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Create / Edit dialog ── */}
      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingYear ? `Edit — ${editingYear}` : 'New Academic Year'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Year *" value={form.year}
            onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
            disabled={!!editingYear}
            fullWidth size="small" sx={{ mb: 3 }}
            placeholder="e.g. 2026"
            helperText={editingYear ? 'Year cannot be changed after creation' : ''}
          />

          <Divider sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Term Dates</Typography>
          </Divider>

          <TextField
            select fullWidth size="small" sx={{ mb: 2.5 }}
            label="Current Term (in session)"
            value={form.terms.find(t => t.isCurrent)?.termNo ?? ''}
            onChange={e => setCurrentTermNo(e.target.value)}
            helperText="Only one term can be current — auto-fills Score Entry & reports"
          >
            <MenuItem value=""><em>— None —</em></MenuItem>
            {form.terms.map(t => <MenuItem key={t.termNo} value={t.termNo}>{t.termName}</MenuItem>)}
          </TextField>

          {form.terms.map(t => (
            <Box key={t.termNo} mb={2.5}>
              <Typography variant="subtitle2" color="primary" gutterBottom>{t.termName}</Typography>
              <Box display="flex" gap={2}>
                <TextField
                  label="Start Date" type="date" size="small"
                  value={t.startDate}
                  onChange={e => updateTerm(t.termNo, 'startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                  error={!!(t.startDate && !isRealDate(t.startDate))}
                  helperText={t.startDate && !isRealDate(t.startDate) ? 'Not a valid date' : ''}
                />
                <TextField
                  label="End Date" type="date" size="small"
                  value={t.endDate}
                  onChange={e => updateTerm(t.termNo, 'endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                  inputProps={{ min: t.startDate || undefined }}
                  error={!!((t.endDate && !isRealDate(t.endDate)) || (t.startDate && t.endDate && t.endDate < t.startDate))}
                  helperText={(t.endDate && !isRealDate(t.endDate)) ? 'Not a valid date' : (t.startDate && t.endDate && t.endDate < t.startDate) ? 'End before start' : ''}
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AcademicYearList;
